// libs
import React, { useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosBack, ArrowIosForward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
// components
import { Stack, Tooltip } from '@lyearn/ui';
// helpers
import { getScores } from '@/modules/corporate/modules/scorecards/components/Skill/helpers/getScores';

import Score from '../Score';
// types
import { ScoreBoxProps } from './types';

const MAX_NUMBER_OF_SCORE_BOXES = 10;

function getInitialStartIndex(arrayLength: number, index: number): number {
  // Find which page the index is roughly in
  let page = Math.floor(index / MAX_NUMBER_OF_SCORE_BOXES);

  let start = page * MAX_NUMBER_OF_SCORE_BOXES;
  let end = start + MAX_NUMBER_OF_SCORE_BOXES;

  // If end is out of bounds, adjust start and end
  if (end > arrayLength) {
    end = arrayLength;
    start = Math.max(0, end - MAX_NUMBER_OF_SCORE_BOXES);
  }

  return start;
}

function ScoreBox(props: ScoreBoxProps) {
  const {
    maxScore,
    minScore,
    score,
    scores: fixedScores,
    onChange,
    disabled = true,
    expectedScore,
    isRepeatedSkill,
  } = props;

  const scores = useMemo(
    () => fixedScores ?? getScores(maxScore, minScore ?? 1, score),
    [fixedScores, maxScore, minScore, score],
  );

  const isMaxScoreOutofRange = maxScore > MAX_NUMBER_OF_SCORE_BOXES;

  const initialStartIndex = !score ? 0 : getInitialStartIndex(scores.length, score - 1);

  const [startIndex, setStartIndex] = useState(initialStartIndex);

  const total = scores.length;
  let start = startIndex;
  let end = start + MAX_NUMBER_OF_SCORE_BOXES;

  // If end exceeds array, adjust start to still get MAX_NUMBER_OF_SCORE_BOXES items
  if (end > total) {
    start = Math.max(total - MAX_NUMBER_OF_SCORE_BOXES, 0); // avoid negative start
    end = total;
  }

  const { t } = useTranslation();
  const [repeatedSkillNudge, setRepeatedSkillNudge] = useState<number | 'close'>();

  const onNudgeOpen = useEvent((value: number | 'close') => {
    setRepeatedSkillNudge((prevValue) =>
      disabled || !isRepeatedSkill || prevValue === 'close' ? prevValue : value,
    );
  });

  const showPrevArrow = isMaxScoreOutofRange && start !== 0;
  const showNextArrow = isMaxScoreOutofRange && end < scores.length;

  return (
    <>
      {showPrevArrow ? (
        <Stack
          alignItems="center"
          className="border border-transparent mr-2 bg-s-info-light rounded-s-8"
          justifyContent="center"
          onClick={() => setStartIndex((prev) => Math.max(prev - MAX_NUMBER_OF_SCORE_BOXES, 0))}>
          <ArrowIosBack height={20} width={20} />
        </Stack>
      ) : null}
      <Stack className="h-40 w-full" gapX={2}>
        {scores.slice(start, end).map((_score, index) => {
          const { value, selected } = _score;
          const isExpectedScore = expectedScore && value === Math.floor(expectedScore);

          const score = (
            <Score
              key={index}
              className={cn(
                'flex-grow',
                isExpectedScore ? 'border border-dashed border-border-dark' : '',
                selected ? 'bg-interactive-primary' : 'bg-s-info-light',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                { 'rounded-s-8': index === 0 && startIndex === 0 },
                { 'rounded-e-8': index === scores.length - 1 },
                { 'hover:bg-interactive-secondary-hover': !(selected || disabled) },
              )}
              disabled={disabled}
              repeatedSkillNudge={{
                show: Boolean(repeatedSkillNudge === value),
                onOpen: (open: boolean) => onNudgeOpen(open ? value : 'close'),
              }}
              selected={selected}
              value={value}
              onChange={onChange}
            />
          );

          return isExpectedScore ? (
            <Tooltip key={index} title={t('Skills:ExpectedLevel')}>
              {score}
            </Tooltip>
          ) : (
            score
          );
        })}
      </Stack>
      {showNextArrow ? (
        <Stack
          alignItems="center"
          className="border border-transparent ml-2 bg-s-info-light rounded-e-8"
          justifyContent="center"
          onClick={() => setStartIndex((prev) => prev + MAX_NUMBER_OF_SCORE_BOXES)}>
          <ArrowIosForward height={20} width={20} />
        </Stack>
      ) : null}
    </>
  );
}

export default ScoreBox;
